import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type {
  AnalyticalTimeseriesResponse,
  GaugingTimeseriesResponse,
  ConcentrationUnit,
} from '@/api/dataVault';
import { CHART_STYLES } from '../../chartTheme';
import { haversineDistance } from './shared/geoUtils';

interface Props {
  data: AnalyticalTimeseriesResponse;
  gaugingData?: GaugingTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

function concColor(ratio: number): string {
  const t = Math.min(Math.max(ratio, 0), 1);
  if (t <= 0.5) {
    const s = t / 0.5;
    const r = Math.round(16 + s * (245 - 16));
    const g = Math.round(185 + s * (158 - 185));
    const b = Math.round(129 + s * (11 - 129));
    return `rgb(${r},${g},${b})`;
  }
  const s = (t - 0.5) / 0.5;
  const r = Math.round(245 + s * (239 - 245));
  const g = Math.round(158 + s * (68 - 158));
  const b = Math.round(11 + s * (68 - 11));
  return `rgb(${r},${g},${b})`;
}

interface WellColumn {
  wellName: string;
  wellId: string;
  concentration: number;
  screenTop: number | null;
  screenBottom: number | null;
  totalDepth: number | null;
  casingElevation: number | null;
  waterElevation: number | null;
  productThickness: number | null;
  color: string;
  circleRadius: number;
  transectPos: number;
  hasConstruction: boolean;
}

interface WellRaw {
  wellName: string;
  wellId: string;
  concentration: number;
  screenTop: number | null;
  screenBottom: number | null;
  totalDepth: number | null;
  casingElevation: number | null;
  waterElevation: number | null;
  productThickness: number | null;
  hasConstruction: boolean;
  latitude: number | null;
  longitude: number | null;
}

interface TooltipState {
  x: number;
  y: number;
  well: WellColumn;
}

const MIN_WELL_SPACING = 80;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.15;

// ── Projection helpers ──────────────────────────────────────────────

/** Compute the "natural" transect bearing (degrees from north) from the longest axis */
function computeNaturalBearing(
  wells: { latitude: number | null; longitude: number | null }[],
): number {
  const hasCoords = wells.filter(w => w.latitude != null && w.longitude != null);
  if (hasCoords.length < 2) return 0;

  let maxDist = 0;
  let axisA = 0;
  let axisB = 1;
  for (let i = 0; i < hasCoords.length; i++) {
    for (let j = i + 1; j < hasCoords.length; j++) {
      const d = haversineDistance(
        hasCoords[i].latitude!, hasCoords[i].longitude!,
        hasCoords[j].latitude!, hasCoords[j].longitude!,
      );
      if (d > maxDist) { maxDist = d; axisA = i; axisB = j; }
    }
  }

  const dLat = hasCoords[axisB].latitude! - hasCoords[axisA].latitude!;
  const dLon = hasCoords[axisB].longitude! - hasCoords[axisA].longitude!;
  // atan2 gives angle from east (math convention). Convert to bearing from north.
  let bearing = (90 - Math.atan2(dLat, dLon) * (180 / Math.PI)) % 360;
  if (bearing < 0) bearing += 360;
  return bearing;
}

/** Project wells onto a transect at a given bearing (degrees from north, clockwise).
 *  Returns normalized 0-1 positions along the transect. */
function projectWellsOnBearing(
  wells: { latitude: number | null; longitude: number | null }[],
  bearingDeg: number,
): number[] {
  const hasCoords = wells.some(w => w.latitude != null && w.longitude != null);
  if (!hasCoords) {
    return wells.map((_, i) => wells.length <= 1 ? 0.5 : i / (wells.length - 1));
  }

  // Convert bearing to unit vector in lat/lon space
  // Bearing 0 = north = +lat direction, 90 = east = +lon direction
  const rad = bearingDeg * (Math.PI / 180);
  const axLat = Math.cos(rad);
  const axLon = Math.sin(rad);

  // Find centroid for reference point
  const coordWells = wells.filter(w => w.latitude != null && w.longitude != null);
  const refLat = coordWells.reduce((s, w) => s + w.latitude!, 0) / coordWells.length;
  const refLon = coordWells.reduce((s, w) => s + w.longitude!, 0) / coordWells.length;

  const projections = wells.map(w => {
    if (w.latitude == null || w.longitude == null) return 0;
    const dLat = w.latitude - refLat;
    const dLon = w.longitude - refLon;
    return dLat * axLat + dLon * axLon;
  });

  const minP = Math.min(...projections);
  const maxP = Math.max(...projections);
  const pRange = maxP - minP || 1;
  return projections.map(p => (p - minP) / pRange);
}


// ── Compass component ───────────────────────────────────────────────

function NorthCompass({ bearingDeg }: { bearingDeg: number }) {
  return (
    <div className="flex flex-col items-center gap-1 select-none">
      <svg width={56} height={56} viewBox="0 0 56 56">
        {/* Outer ring */}
        <circle cx={28} cy={28} r={26} fill="none" stroke="var(--dv-border-subtle)" strokeWidth={1.5} />
        <circle cx={28} cy={28} r={26} fill="rgba(0,0,0,0.3)" />

        {/* Tick marks every 45 degrees */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
          const r1 = 22;
          const r2 = 26;
          const rad = (deg - bearingDeg) * (Math.PI / 180) - Math.PI / 2;
          return (
            <line
              key={deg}
              x1={28 + Math.cos(rad) * r1}
              y1={28 + Math.sin(rad) * r1}
              x2={28 + Math.cos(rad) * r2}
              y2={28 + Math.sin(rad) * r2}
              stroke="var(--dv-text-muted)"
              strokeWidth={deg % 90 === 0 ? 2 : 1}
              opacity={deg % 90 === 0 ? 0.8 : 0.4}
            />
          );
        })}

        {/* North arrow — always points to true north relative to the transect rotation */}
        <g transform={`rotate(${-bearingDeg}, 28, 28)`}>
          {/* North triangle (red) */}
          <polygon points="28,4 24,20 32,20" fill="#ef4444" opacity={0.9} />
          {/* South triangle (white) */}
          <polygon points="28,52 24,36 32,36" fill="var(--dv-text-muted)" opacity={0.4} />
          {/* N label */}
          <text x={28} y={14} textAnchor="middle" fontSize={7} fontWeight={700} fill="#fff">N</text>
        </g>

        {/* Transect line indicator (horizontal = the slice direction) */}
        <line x1={4} y1={28} x2={52} y2={28} stroke="#22d3ee" strokeWidth={1.5} opacity={0.5} strokeDasharray="3 2" />

        {/* Center dot */}
        <circle cx={28} cy={28} r={2} fill="#22d3ee" />
      </svg>

      {/* Bearing readout */}
      <div className="text-[11px] font-mono tabular-nums tracking-wider" style={{ color: '#22d3ee' }}>
        {Math.round(bearingDeg)}°
      </div>
    </div>
  );
}


// ── Main component ──────────────────────────────────────────────────

export function CrossSectionChart({ data, gaugingData, unit }: Props) {
  /* ── Analyte list ─────────────────────────────────────────────── */
  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    const wells = data?.wells ?? [];
    for (const well of wells) {
      for (const [code, analyte] of Object.entries(well.analytes ?? {})) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data?.wells]);

  const defaultAnalyte = useMemo(() => {
    const benzene = allAnalytes.find(a => a.compound.toLowerCase() === 'benzene');
    return benzene?.code ?? allAnalytes[0]?.code ?? '';
  }, [allAnalytes]);

  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(defaultAnalyte);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  /* ── Pan / Zoom state ────────────────────────────────────────── */
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  /* ── Rotation state ──────────────────────────────────────────── */
  const [isRotating, setIsRotating] = useState(false);
  const rotateStart = useRef({ clientX: 0, clientY: 0, startBearing: 0 });

  /* ── Build water level lookup ────────────────────────────────── */
  const waterLevelMap = useMemo(() => {
    const map = new Map<string, { waterElev: number | null; productThickness: number | null }>();
    if (!gaugingData?.wells?.length) return map;
    for (const well of gaugingData.wells) {
      const measurements = [...(well.measurements ?? [])].reverse();
      const latest = measurements.find(m => m.water_elevation_ft != null);
      const latestPt = measurements.find(m => m.product_thickness_ft != null && m.product_thickness_ft > 0);
      map.set(well.well_name, {
        waterElev: latest?.water_elevation_ft ?? null,
        productThickness: latestPt?.product_thickness_ft ?? null,
      });
    }
    return map;
  }, [gaugingData]);

  /* ── Build raw well data (independent of rotation) ────────────── */
  const { rawWells, maxConc, depthRange } = useMemo(() => {
    const cols: WellRaw[] = [];
    let max = 0;

    const wellsList = data?.wells ?? [];
    for (const well of wellsList) {
      const analyteData = well.analytes?.[selectedAnalyte];
      if (!analyteData) continue;

      const measurements = analyteData.measurements ?? [];
      if (!measurements.length) continue;

      const values = measurements
        .map(m => convertConc(m.concentration, unit))
        .filter((v): v is number => v !== null);
      if (!values.length) continue;

      const latest = convertConc(
        measurements[measurements.length - 1].concentration,
        unit,
      );
      const conc = latest ?? 0;
      if (conc > max) max = conc;

      const gauging = waterLevelMap.get(well.well_name);

      cols.push({
        wellName: well.well_name,
        wellId: well.well_id,
        concentration: conc,
        screenTop: well.screen_top_ft ?? null,
        screenBottom: well.screen_bottom_ft ?? null,
        totalDepth: well.total_depth_ft ?? null,
        casingElevation: well.casing_elevation_ft ?? null,
        waterElevation: gauging?.waterElev ?? null,
        productThickness: gauging?.productThickness ?? null,
        hasConstruction: well.screen_top_ft != null && well.screen_bottom_ft != null,
        latitude: well.latitude ?? null,
        longitude: well.longitude ?? null,
      });
    }

    let minDepth = 0;
    let maxDepth = 100;
    const depthValues: number[] = [];
    for (const c of cols) {
      if (c.screenTop != null) depthValues.push(c.screenTop);
      if (c.screenBottom != null) depthValues.push(c.screenBottom);
      if (c.totalDepth != null) depthValues.push(c.totalDepth);
    }
    if (depthValues.length > 0) {
      minDepth = 0;
      maxDepth = Math.ceil((Math.max(...depthValues) + 10) / 10) * 10;
    }

    return { rawWells: cols, maxConc: max > 0 ? max : 1, depthRange: { min: minDepth, max: maxDepth } };
  }, [data?.wells, selectedAnalyte, unit, waterLevelMap]);

  /* ── Natural bearing (auto-detected from well spread) ────────── */
  const naturalBearing = useMemo(() => computeNaturalBearing(rawWells), [rawWells]);
  const [bearing, setBearing] = useState<number | null>(null); // null = use natural
  const activeBearing = bearing ?? naturalBearing;

  // Reset bearing when analyte changes
  useEffect(() => { setBearing(null); }, [selectedAnalyte]);

  /* ── Project wells onto current bearing ──────────────────────── */
  const wellColumns: WellColumn[] = useMemo(() => {
    const positions = projectWellsOnBearing(rawWells, activeBearing);
    const effective = maxConc;
    return rawWells.map((col, i) => {
      const ratio = col.concentration / effective;
      return {
        ...col,
        color: concColor(ratio),
        circleRadius: 4 + ratio * 12,
        transectPos: positions[i],
      };
    });
  }, [rawWells, activeBearing, maxConc]);

  /* ── SVG layout ──────────────────────────────────────────────── */
  const MARGIN = { top: 50, right: 40, bottom: 30, left: 55 };
  const SVG_H = 420;
  const naturalWidth = 800;
  const neededWidth = MARGIN.left + MARGIN.right + wellColumns.length * MIN_WELL_SPACING;
  const SVG_W = Math.max(naturalWidth, neededWidth);
  const plotW = SVG_W - MARGIN.left - MARGIN.right;
  const plotH = SVG_H - MARGIN.top - MARGIN.bottom;

  const yScale = useCallback(
    (depth: number) => MARGIN.top + ((depth - depthRange.min) / (depthRange.max - depthRange.min)) * plotH,
    [plotH, depthRange],
  );

  const xForWell = useCallback(
    (idx: number) => {
      if (wellColumns.length <= 1) return MARGIN.left + plotW / 2;
      const pos = wellColumns[idx]?.transectPos ?? (idx / (wellColumns.length - 1));
      return MARGIN.left + pos * plotW;
    },
    [wellColumns, plotW],
  );

  /* ── Water table line ────────────────────────────────────────── */
  const waterTableLine = useMemo(() => {
    const points: { transectPos: number; depthBGS: number }[] = [];
    for (const well of wellColumns) {
      if (well.casingElevation != null && well.waterElevation != null) {
        points.push({ transectPos: well.transectPos, depthBGS: well.casingElevation - well.waterElevation });
      }
    }
    if (points.length < 2) return null;
    points.sort((a, b) => a.transectPos - b.transectPos);
    return points.map(p => `${MARGIN.left + p.transectPos * plotW},${yScale(p.depthBGS)}`).join(' ');
  }, [wellColumns, plotW, yScale]);

  const avgWtDepth = useMemo(() => {
    const depths = wellColumns
      .filter(w => w.casingElevation != null && w.waterElevation != null)
      .map(w => w.casingElevation! - w.waterElevation!);
    return depths.length ? depths.reduce((a, b) => a + b, 0) / depths.length : null;
  }, [wellColumns]);

  /* ── Depth axis ticks ────────────────────────────────────────── */
  const depthTicks = useMemo(() => {
    const range = depthRange.max - depthRange.min;
    const step = range <= 50 ? 10 : range <= 100 ? 20 : 25;
    const ticks: number[] = [];
    for (let d = depthRange.min; d <= depthRange.max; d += step) ticks.push(d);
    return ticks;
  }, [depthRange]);

  const constructionCount = wellColumns.filter(w => w.hasConstruction).length;

  /* ── Chart interaction: left-drag = rotate, middle/right-drag = pan, scroll = zoom ── */

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(z => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta));
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const scale = next / z;
        setPanX(px => cx - scale * (cx - px));
        setPanY(py => cy - scale * (cy - py));
      }
      return next;
    });
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    if (e.button === 0) {
      // Left click = rotate transect
      setIsRotating(true);
      rotateStart.current = { clientX: e.clientX, clientY: e.clientY, startBearing: activeBearing };
    } else if (e.button === 1 || e.button === 2) {
      // Middle or right click = pan
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, panX, panY };
    }
  }, [panX, panY, activeBearing]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (isRotating) {
      const dx = e.clientX - rotateStart.current.clientX;
      let newBearing = (rotateStart.current.startBearing - dx * 0.5) % 360;
      if (newBearing < 0) newBearing += 360;
      setBearing(newBearing);
    }
    if (isPanning) {
      setPanX(panStart.current.panX + (e.clientX - panStart.current.x));
      setPanY(panStart.current.panY + (e.clientY - panStart.current.y));
    }
  }, [isRotating, isPanning]);

  const handlePointerUp = useCallback(() => {
    setIsRotating(false);
    setIsPanning(false);
  }, []);

  const handleResetView = useCallback(() => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setBearing(null);
  }, []);

  useEffect(() => { setZoom(1); setPanX(0); setPanY(0); }, [selectedAnalyte]);

  /* ── Empty state ──────────────────────────────────────────────── */
  if (!data || !data.wells || !data.wells.length || !allAnalytes.length || !wellColumns.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
    <div>
      {/* Header: Analyte selector + Compass + Zoom */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium" style={{ color: 'var(--dv-text-muted)' }}>Analyte</label>
          <select
            value={selectedAnalyte}
            onChange={e => setSelectedAnalyte(e.target.value)}
            className="text-xs rounded px-2 py-1 border"
            style={{ background: 'var(--dv-bg-card)', borderColor: 'var(--dv-border-subtle)', color: 'var(--dv-text-primary)' }}
          >
            {allAnalytes.map(a => (
              <option key={a.code} value={a.code}>{a.compound}</option>
            ))}
          </select>
        </div>

        {/* Compass — display only, rotates with chart drag */}
        <NorthCompass bearingDeg={activeBearing} />

        {/* Zoom controls */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-mono-500 mr-1">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP * 2))}
            className="w-6 h-6 flex items-center justify-center rounded border text-xs hover:bg-mono-800 transition-colors"
            style={{ borderColor: 'var(--dv-border-subtle)', color: 'var(--dv-text-muted)' }}
          >-</button>
          <button
            onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP * 2))}
            className="w-6 h-6 flex items-center justify-center rounded border text-xs hover:bg-mono-800 transition-colors"
            style={{ borderColor: 'var(--dv-border-subtle)', color: 'var(--dv-text-muted)' }}
          >+</button>
          <button
            onClick={handleResetView}
            className="h-6 px-2 flex items-center justify-center rounded border text-[10px] hover:bg-mono-800 transition-colors tracking-wider"
            style={{ borderColor: 'var(--dv-border-subtle)', color: 'var(--dv-text-muted)' }}
          >FIT</button>
        </div>
      </div>

      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Drag left/right to rotate the transect. Scroll to zoom. Middle-click drag to pan.
        {constructionCount < wellColumns.length && (
          <span className="text-amber-400 ml-1">
            ({wellColumns.length - constructionCount} of {wellColumns.length} wells missing screen data)
          </span>
        )}
      </p>

      {/* Chart viewport */}
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-lg"
        style={{ height: 420, cursor: isRotating ? 'ew-resize' : isPanning ? 'grabbing' : 'ew-resize', userSelect: 'none' }}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onContextMenu={(e) => e.preventDefault()}
      >
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          width={SVG_W}
          height={SVG_H}
          style={{
            transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: isPanning ? 'none' : 'transform 0.15s ease-out',
          }}
          role="img"
          aria-label="Cross-section view of subsurface contamination"
        >
          <defs>
            <pattern id="cs-hatch" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="4" stroke="var(--dv-text-muted)" strokeWidth="0.8" opacity="0.5" />
            </pattern>
            <pattern id="cs-psh" width="4" height="4" patternUnits="userSpaceOnUse">
              <rect width="4" height="4" fill="#f59e0b" opacity="0.3" />
              <line x1="0" y1="2" x2="4" y2="2" stroke="#f59e0b" strokeWidth="0.5" opacity="0.6" />
            </pattern>
          </defs>

          {/* Ground surface */}
          <line x1={MARGIN.left} y1={yScale(0)} x2={MARGIN.left + plotW} y2={yScale(0)} stroke="#8B6914" strokeWidth={3} />
          <text x={MARGIN.left - 8} y={yScale(0) + 4} textAnchor="end" fontSize={9} fill="var(--dv-text-muted)">Ground</text>

          {/* Grid lines */}
          {depthTicks.map(d => (
            <line key={d} x1={MARGIN.left} y1={yScale(d)} x2={MARGIN.left + plotW} y2={yScale(d)}
              stroke="var(--dv-chart-grid)" strokeDasharray={CHART_STYLES.grid.strokeDasharray} opacity={0.4} />
          ))}

          {/* Y-axis labels */}
          {depthTicks.map(d => (
            <text key={d} x={MARGIN.left - 8} y={yScale(d) + 4} textAnchor="end"
              fontSize={CHART_STYLES.axisTick.fontSize} fill="var(--dv-chart-axis)">{d} ft</text>
          ))}

          {/* Water table line */}
          {waterTableLine && (
            <>
              <polyline points={waterTableLine} fill="none" stroke="#3b82f6" strokeWidth={2} strokeDasharray="8 4" opacity={0.7} />
              <text x={MARGIN.left + plotW + 4} y={avgWtDepth != null ? yScale(avgWtDepth) + 4 : yScale(20) + 4}
                fontSize={9} fill="#3b82f6">WT</text>
            </>
          )}

          {/* Well columns */}
          {wellColumns.map((well, i) => {
            const cx = xForWell(i);

            let screenTopDepth: number;
            let screenBottomDepth: number;
            if (well.screenTop != null && well.screenBottom != null) {
              screenTopDepth = well.screenTop;
              screenBottomDepth = well.screenBottom;
            } else if (well.totalDepth != null) {
              screenTopDepth = well.totalDepth * 0.65;
              screenBottomDepth = well.totalDepth;
            } else {
              screenTopDepth = depthRange.max * 0.4;
              screenBottomDepth = depthRange.max * 0.7;
            }

            const screenTopY = yScale(screenTopDepth);
            const screenBottomY = yScale(screenBottomDepth);
            const screenMidY = (screenTopY + screenBottomY) / 2;
            const casingTopY = yScale(0);
            const casingBottomY = yScale(well.totalDepth ?? screenBottomDepth + 5);

            let waterDepthY: number | null = null;
            if (well.casingElevation != null && well.waterElevation != null) {
              waterDepthY = yScale(well.casingElevation - well.waterElevation);
            }

            let pshTopY: number | null = null;
            let pshBottomY: number | null = null;
            if (waterDepthY != null && well.productThickness != null && well.productThickness > 0) {
              const waterDepthBGS = well.casingElevation! - well.waterElevation!;
              pshTopY = yScale(waterDepthBGS - well.productThickness);
              pshBottomY = waterDepthY;
            }

            return (
              <g
                key={well.wellName}
                onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, well })}
                onMouseMove={(e) => { if (tooltip) setTooltip({ x: e.clientX, y: e.clientY, well }); }}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Well label — rotated */}
                <text x={cx} y={MARGIN.top - 8} textAnchor="start" fontSize={10} fontWeight={600}
                  fill="var(--dv-text-primary)" transform={`rotate(-45, ${cx}, ${MARGIN.top - 8})`}>
                  {well.wellName}
                </text>

                {!well.hasConstruction && (
                  <text x={cx + 4} y={MARGIN.top + 8} textAnchor="middle" fontSize={7} fill="#f59e0b" opacity={0.7}>*</text>
                )}

                {/* Casing */}
                <line x1={cx} y1={casingTopY} x2={cx} y2={casingBottomY} stroke="#9ca3af" strokeWidth={2} />

                {/* Screen */}
                <rect x={cx - 3} y={screenTopY} width={6} height={screenBottomY - screenTopY}
                  fill="url(#cs-hatch)" stroke="#9ca3af" strokeWidth={1} />

                {/* PSH */}
                {pshTopY != null && pshBottomY != null && (
                  <rect x={cx - 5} y={pshTopY} width={10} height={pshBottomY - pshTopY}
                    fill="url(#cs-psh)" stroke="#f59e0b" strokeWidth={0.5} opacity={0.8} />
                )}

                {/* Water level tick */}
                {waterDepthY != null && (
                  <line x1={cx - 6} y1={waterDepthY} x2={cx + 6} y2={waterDepthY} stroke="#3b82f6" strokeWidth={2} />
                )}

                {/* Concentration circle */}
                <circle cx={cx} cy={screenMidY} r={well.circleRadius}
                  fill={well.color} opacity={0.85} stroke="var(--dv-bg-card)" strokeWidth={1.5} />
              </g>
            );
          })}
        </svg>

        {/* Minimap */}
        {zoom > 1.1 && (
          <div className="absolute bottom-2 right-2 rounded border overflow-hidden"
            style={{ width: 120, height: Math.round(120 * (SVG_H / SVG_W)), background: 'rgba(0,0,0,0.6)',
              borderColor: 'var(--dv-border-subtle)', pointerEvents: 'none' }}>
            <div className="absolute border" style={{
              borderColor: '#22d3ee', borderWidth: 1,
              left: `${Math.max(0, (-panX / (SVG_W * zoom)) * 100)}%`,
              top: `${Math.max(0, (-panY / (SVG_H * zoom)) * 100)}%`,
              width: `${Math.min(100, (1 / zoom) * 100)}%`,
              height: `${Math.min(100, (1 / zoom) * 100)}%`,
            }} />
          </div>
        )}

        {/* Tooltip */}
        {tooltip && (
          <div className="fixed z-50 pointer-events-none rounded-lg px-3.5 py-3 shadow-xl"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10, background: 'var(--dv-tooltip-bg)',
              backdropFilter: 'blur(8px)', border: '1px solid var(--dv-border-subtle)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
            <div className="text-[10px] tracking-wider mb-2 pb-2"
              style={{ color: 'var(--dv-text-muted)', borderBottom: '1px solid var(--dv-border-subtle)' }}>
              {tooltip.well.wellName}
              {!tooltip.well.hasConstruction && <span className="text-amber-400 ml-1">(estimated depths)</span>}
            </div>
            <div className="space-y-1 text-[11px]" style={{ color: 'var(--dv-text-primary)' }}>
              {tooltip.well.screenTop != null && tooltip.well.screenBottom != null && (
                <div>Screen: <span className="font-mono">{tooltip.well.screenTop.toFixed(1)} - {tooltip.well.screenBottom.toFixed(1)} ft</span></div>
              )}
              {tooltip.well.totalDepth != null && (
                <div>Total Depth: <span className="font-mono">{tooltip.well.totalDepth.toFixed(1)} ft</span></div>
              )}
              {tooltip.well.casingElevation != null && tooltip.well.waterElevation != null && (
                <div>Water Level: <span className="font-mono text-blue-400">
                  {(tooltip.well.casingElevation - tooltip.well.waterElevation).toFixed(1)} ft BGS
                </span>
                <span className="text-mono-400 ml-1">({tooltip.well.waterElevation.toFixed(1)} ft MSL)</span></div>
              )}
              {tooltip.well.productThickness != null && tooltip.well.productThickness > 0 && (
                <div>PSH: <span className="font-mono text-amber-400">{tooltip.well.productThickness.toFixed(3)} ft</span></div>
              )}
              <div>Concentration: <span className="font-mono" style={{ color: tooltip.well.color }}>
                {tooltip.well.concentration.toFixed(4)} {unit}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
